/**
 * Pure (DOM-free, unit-testable) presentation model for the Research Run/Progress
 * screen (`ResearchRunView`). Maps the backend orchestrator state
 * (`ResearchStatusResponse` — job.status + job.currentStage + counters) onto the
 * prototype's 5-stage rail and its progress/pill/edge-state presentation.
 *
 * 1:1 parity source — the prototype run screen
 * `../EER app/prototype/assets/research.proto.js` (renderRun / renderRunEdge /
 * runStepper, lines 624-779): the rail is Queued → Discovering →
 * Verifying About-Us → Scoring & ranking → Ready for review; a progress bar with
 * "<scored> of <total> companies scored · <discovered> discovered · <errors>
 * errors"; plus the two edge states — `failed` (crash + resume/retry) and
 * `empty`/low-yield (rare geo, suggested relaxations).
 *
 * The prototype had a fake 7-state screenshot driver; here the state is DERIVED
 * from the real backend snapshot instead of a mock — no fabricated progress.
 */
import type { IntakeCriteria, JobStatus, OrchestratorStage } from '@eer/shared';

// ---------------------------------------------------------------------------
// The 5 rail stages (prototype `MOCK.run.stages` / `stateLabels`)
// ---------------------------------------------------------------------------

/** The rail keys, left → right (prototype order). */
export const RUN_RAIL_STAGES = ['queued', 'discovering', 'verifying', 'scoring', 'ready'] as const;
export type RunRailStage = (typeof RUN_RAIL_STAGES)[number];

/** Human labels for each rail stage (verbatim from the prototype `stateLabels`). */
export const RUN_RAIL_LABELS: Record<RunRailStage, string> = {
  queued: 'Queued',
  discovering: 'Discovering',
  verifying: 'Verifying About-Us',
  scoring: 'Scoring & ranking',
  ready: 'Ready for review',
};

// ---------------------------------------------------------------------------
// The overall run presentation state (drives the pill + which screen body shows)
// ---------------------------------------------------------------------------

/**
 * The presentation state of a run:
 *   - `queued|discovering|verifying|scoring` → the live progress screen (working);
 *   - `ready`  → progress screen with the "Review long-list" gate unlocked;
 *   - `failed` → the crash/resume edge screen;
 *   - `empty`  → the low-yield edge screen (rare geo / narrow niche).
 */
export type RunPresentationState = RunRailStage | 'failed';

/**
 * Map a run-level orchestrator stage (10 values) onto the 5-stage rail. The
 * pre-discovery stages (normalize/intake) collapse to `discovering`; dedup /
 * disambiguation / enrichment / verify all present as `verifying`; scoring stays
 * `scoring`; review/publish mean the long-list is ready.
 */
export function railStageFor(stage: OrchestratorStage | null): RunRailStage {
  switch (stage) {
    case null:
    case 'intake':
      return 'queued';
    case 'normalize':
    case 'discovery':
      return 'discovering';
    case 'dedup':
    case 'disambiguation':
    case 'enrichment':
    case 'verify':
      return 'verifying';
    case 'scoring':
      return 'scoring';
    case 'review':
    case 'publish':
      return 'ready';
    default:
      return 'queued';
  }
}

/**
 * Derive the overall presentation state from the job status + current stage.
 *   - failed job                       → `failed` edge screen;
 *   - completed / paused (at gate)     → `ready` (long-list is ready to review);
 *   - queued job                       → `queued`;
 *   - running job                      → the rail stage of its current stage.
 * (`cancelled` renders as its current rail position — the Cancel action itself
 * routes the user back to the library, so a cancelled run is rarely rendered.)
 */
export function runPresentationState(
  status: JobStatus,
  stage: OrchestratorStage | null,
): RunPresentationState {
  if (status === 'failed') return 'failed';
  if (status === 'completed' || status === 'paused') return 'ready';
  if (status === 'queued') return 'queued';
  return railStageFor(stage);
}

/** Index of a rail stage (for the "done vs active vs pending" step styling). */
export function railStageIndex(state: RunPresentationState): number {
  // `failed` is not on the rail; pin it to the front so no step reads as done.
  if (state === 'failed') return -1;
  return RUN_RAIL_STAGES.indexOf(state);
}

// ---------------------------------------------------------------------------
// E06-F05-T14 — honest rail when discovery finished but nothing is scored yet
// ---------------------------------------------------------------------------

/**
 * Discovery is free and stops before scoring (E06-F05-T04): a finished run is
 * `ready` while `scored === 0` until someone runs the manual, paid AI check. In
 * that state the "Scoring & ranking" step must NOT read as done (the reported
 * "0 of 891 scored" next to a green check) — it is waiting for the AI check.
 */
export function scoringAwaitsAiCheck(state: RunPresentationState, scored: number): boolean {
  return state === 'ready' && scored <= 0;
}

/** Per-step rail status: done / active / pending / awaiting (AI check not run yet). */
export type RailStepStatus = 'done' | 'active' | 'pending' | 'awaiting';

export function railStepStatus(
  stage: RunRailStage,
  state: RunPresentationState,
  scored: number,
): RailStepStatus {
  if (stage === 'scoring' && scoringAwaitsAiCheck(state, scored)) return 'awaiting';
  const activeIdx = railStageIndex(state);
  const idx = RUN_RAIL_STAGES.indexOf(stage);
  if (idx === activeIdx) return 'active';
  return activeIdx > idx ? 'done' : 'pending';
}

/** Status-pill text: "Ready for review" only once something is actually scored. */
export function runStatusLabel(state: RunPresentationState, scored: number): string {
  if (state !== 'ready') return 'Working…';
  return scoringAwaitsAiCheck(state, scored)
    ? 'Discovery done — AI check next'
    : 'Ready for review';
}

/**
 * The 4-step research process explained on Run + AI check:
 * discovery (free) → AI check (manual, paid) → scoring → review.
 */
export const RESEARCH_PROCESS_STEPS = ['discovery', 'aicheck', 'scoring', 'review'] as const;
export type ResearchProcessStep = (typeof RESEARCH_PROCESS_STEPS)[number];

/**
 * Which process step the Run screen is on: still searching → discovery; finished
 * with 0 scored → AI check is the next action; finished with scored rows → review.
 */
export function processStepForRun(
  state: RunPresentationState,
  scored: number,
): ResearchProcessStep {
  if (state !== 'ready') return 'discovery';
  return scored > 0 ? 'review' : 'aicheck';
}

/**
 * Integer progress percent (0..100). Guards a zero total (queued, before the
 * long-list is sized) so the bar never shows NaN. When the run is `ready`/done
 * the bar is pinned to 100% even if a late counter lags.
 */
export function progressPercent(completed: number, total: number, ready: boolean): number {
  if (ready) return 100;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
}

/**
 * A run is "low yield" (the empty/rare-geo edge) when it FINISHED
 * (completed/paused, i.e. `ready`) but discovered very few companies. The
 * threshold mirrors the prototype's rare-geo example ("Only N companies found").
 */
export const LOW_YIELD_THRESHOLD = 10 as const;

export function isLowYield(state: RunPresentationState, total: number): boolean {
  return state === 'ready' && total > 0 && total < LOW_YIELD_THRESHOLD;
}

/** The suggested-relaxation chips shown on the low-yield edge (prototype verbatim). */
export const SUGGESTED_RELAXATIONS: readonly string[] = [
  'Radius 50 → 150 mi',
  'Size 50–500 → 50–2000',
  'Add adjacent sub-industries',
  'Include Grade B pool',
];

// ---------------------------------------------------------------------------
// E06-F02-T10 — the request criteria recap shown ON the Run screen
// ---------------------------------------------------------------------------

/**
 * One "label: value" line of the criteria recap. The Run screen renders these as
 * a definition grid so the recruiter always sees WHICH request the run belongs to
 * — including when the screen is re-entered for a finished (`in_review`) request,
 * where nothing else on the page carries the criteria.
 */
export interface CriteriaChip {
  readonly key: string;
  readonly label: string;
  readonly value: string;
}

/** Human labels for the machine-valued intake purpose. */
const PURPOSE_LABELS: Record<string, string> = {
  sourcing: 'Sourcing (ranked long-list)',
  market_mapping: 'Market mapping (exhaustive)',
};

/** Human labels for the verification strictness. */
const VERIFY_LABELS: Record<string, string> = {
  strict: 'Strict',
  lenient: 'Lenient',
};

/** "Berlin (50 km)" / "Germany" — a location with its metro radius when set. */
export function locationLabel(loc: { label: string; radiusKm?: number | null }): string {
  const radius = loc.radiusKm;
  return radius != null && radius > 0 ? `${loc.label} (${radius} km)` : loc.label;
}

/**
 * E07-F04-T16 — the locations line. A single-stage request (every pre-E06-F01-T10
 * location has no `stage` = stage 1) stays a flat list: "Berlin (50 km), Germany".
 * With 2+ stages the order is part of the request ("Europe first, then Global"), so
 * the line groups by stage: "Stage 1: Germany, France · Stage 2: Global".
 */
export function locationsSummary(
  locations: readonly { label: string; radiusKm?: number | null; stage?: number }[],
): string {
  const byStage = new Map<number, string[]>();
  for (const loc of locations) {
    const label = locationLabel(loc).trim();
    if (label.length === 0) continue;
    const stage = loc.stage ?? 1;
    const list = byStage.get(stage) ?? [];
    list.push(label);
    byStage.set(stage, list);
  }
  const stages = [...byStage.keys()].sort((a, b) => a - b);
  if (stages.length <= 1) return stages.flatMap((s) => byStage.get(s) ?? []).join(', ');
  return stages.map((s) => `Stage ${s}: ${(byStage.get(s) ?? []).join(', ')}`).join(' · ');
}

/** Join a list, dropping blanks; '' when nothing survives. */
function joinList(values: readonly string[] | undefined): string {
  return (values ?? [])
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .join(', ');
}

/** "2010–2020" / "from 2010" / "until 2020" / '' when neither bound is set. */
export function foundedRange(from: number | null, to: number | null): string {
  if (from != null && to != null) return `${from}–${to}`;
  if (from != null) return `from ${from}`;
  if (to != null) return `until ${to}`;
  return '';
}

/**
 * Derive the criteria recap from the stored intake criteria. Empty fields are
 * dropped (a partially-filled draft shows only what it has); a missing criteria
 * object yields an empty list, which the screen renders as the explicit
 * "criteria unavailable" note rather than a silent blank.
 */
export function criteriaChips(
  criteria: IntakeCriteria | null | undefined,
): readonly CriteriaChip[] {
  if (!criteria) return [];
  const chips: CriteriaChip[] = [];
  const push = (key: string, label: string, value: string): void => {
    if (value.trim().length > 0) chips.push({ key, label, value });
  };

  push('client', 'Client', criteria.clientName ?? '');
  push('industry', 'Industry', criteria.industry ?? '');
  push('subCategories', 'Sub-categories', joinList(criteria.subCategories));
  push('locations', 'Locations', locationsSummary(criteria.locations ?? []));
  // E07-F04-T16 — excluded from every location stage ("Global excl. UK").
  push('locationExclusions', 'Excluded locations', joinList(criteria.locationExclusions));
  push(
    'founded',
    'Founded',
    foundedRange(criteria.foundedFrom ?? null, criteria.foundedTo ?? null),
  );
  push('sizeGlobal', 'Size (global)', criteria.sizeGlobal ?? '');
  push(
    'sizeLocalCap',
    'Local headcount cap',
    criteria.sizeLocalCap == null ? '' : String(criteria.sizeLocalCap),
  );
  push('companyTypes', 'Company types', joinList(criteria.companyTypes));
  push('fundingStages', 'Funding', joinList(criteria.fundingStages));
  push('competitors', 'Reference / competitors', joinList(criteria.competitors));
  push(
    'dns',
    'Do-not-source',
    joinList([...(criteria.dnsCompanies ?? []), ...(criteria.dnsIndustries ?? [])]),
  );
  // E07-F04-T16 — the brief's "Avoid" business models (own field since E06-F01-T10).
  push('avoid', 'Avoid', joinList(criteria.avoid));
  push('purpose', 'Purpose', PURPOSE_LABELS[criteria.purpose] ?? criteria.purpose ?? '');
  push('gradeScope', 'Grade scope', criteria.gradeScope ?? '');
  push(
    'verifyStrictness',
    'Verification',
    VERIFY_LABELS[criteria.verifyStrictness] ?? criteria.verifyStrictness ?? '',
  );
  push(
    'targetCount',
    'Target count',
    criteria.targetCount == null ? '' : String(criteria.targetCount),
  );

  return chips;
}
